import { Box, Stack } from "@mui/material";
import { SubNavAppBar } from "components/AppBar";
import { FacilityPicker } from "components/FacilityPicker";

/**
 * The page-level facility scope bar that stays put while the page scrolls.
 *
 * Facility scope lives here rather than in an omnisearch box because it governs
 * the whole page — headline tiles count what this bar selects, not what a grid's
 * own filters narrow to — and because it has to survive scrolling past the
 * things that answer to it.
 *
 * Shared across the waste pages that are read one facility at a time, so
 * changing scope is the same control in the same place on each of them: a
 * container list, a shipment list, and the packaging page a shipment is built
 * on are one task, and hunting for a differently-shaped picker between them is
 * the reader's problem, not the page's.
 *
 * The selection is held in the URL (`?facilityId=`), so a scoped page is
 * linkable and the browser's back button steps through scopes.
 */
export function WasteFacilityScopeBar({
  facilityId,
  onChangeFacility,
  placeholder = "All Facilities",
}: {
  facilityId: string | null;
  /** Null clears the scope back to every facility. */
  onChangeFacility: (facilityId: string | null) => void;
  /**
   * What the unscoped state reads as. Defaults to "All Facilities" — the pages
   * that can show every facility at once. Packaging cannot: a shipment belongs
   * to one facility, so that page asks for one instead of implying it could
   * proceed without it.
   */
  placeholder?: string;
}) {
  return (
    <SubNavAppBar
      sx={{
        py: 2,
        px: 3,
        backgroundColor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} alignItems="flex-end" spacing={3}>
        {/* Wide enough to read a real facility name. These run long — "QA
            Container Tracking — LQG Plant (612 Everardo Passage)" is 55
            characters — and at the old 240px the control truncated to "QA
            Container Tracking …", which named the tenant and not the facility.
            A scope control the reader cannot read is not a scope control. */}
        <Box sx={{ width: { xs: "100%", sm: 360, lg: 460 } }}>
          {/* The picker is built for react-hook-form; as a filter it has no
              field state to report, and nothing here can be invalid — clearing
              it is a valid scope, not an empty required field. */}
          <FacilityPicker
            label="Facility"
            facilityQueryVariant="waste"
            value={facilityId}
            onChange={(facility) => onChangeFacility(facility?.id ?? null)}
            textFieldProps={{
              // The specs draw a standard (underlined) select here, not the
              // outlined box the forms use — this is a page-level scope
              // control, and an outlined field reads as something to fill in.
              variant: "standard",
              placeholder,
              // Shrink unconditionally so the unscoped state reads as its
              // placeholder rather than an empty field the reader has to
              // interpret.
              InputLabelProps: { shrink: true },
              // "All Facilities" is the scope in force, not a prompt to fill
              // the field in, so it is drawn at full strength like any other
              // selected value. MUI dims placeholders to ~42% by default,
              // which made the unscoped state read as disabled.
              sx: {
                "& input::placeholder": {
                  color: "text.primary",
                  opacity: 1,
                },
              },
            }}
            showErrors={false}
            invalid={false}
            isTouched={false}
            isDirty={false}
            isValidating={false}
          />
        </Box>
      </Stack>
    </SubNavAppBar>
  );
}
